import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import AnimatedLoadingSkeleton from "./animated-loading-skeleton";

interface PlanUpgradePromptProps {
  /**
   * The name of the feature to display in the prompt
   */
  featureName: string;
  /**
   * Optional custom description for the feature
   */
  description?: string;
  /**
   * Optional class names to apply to the card
   */
  className?: string;
}

/**
 * A component that displays a prompt to upgrade when a feature is not available in the current plan
 */
export function PlanUpgradePrompt({ 
  featureName, 
  description,
  className = ''
}: PlanUpgradePromptProps) {
  const navigate = useNavigate();

  const defaultDescription = `The ${featureName} feature is not available on your current plan. Upgrade to a higher tier plan to access this feature.`;

  return (
    <div className="relative min-h-[600px] w-full">
      {/* Background loading skeleton with full width */}
      <div className="absolute inset-0 w-full h-full flex items-center justify-center overflow-hidden">
        <div className="w-full transform scale-110">
          <AnimatedLoadingSkeleton />
        </div>
      </div>
      
      {/* Content overlay with lighter blur */}
      <div className="absolute inset-0 bg-white/20 backdrop-blur-sm" />
      
      {/* Upgrade prompt card */}
      <div className="relative z-10">
        <Card className={`w-full max-w-3xl mx-auto my-12 bg-white ${className}`}>
          <CardHeader>
            <CardTitle className="text-2xl">Feature Not Available</CardTitle>
            <CardDescription>Upgrade required to access {featureName}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p>{description || defaultDescription}</p>
              
              <div className="bg-muted p-4 rounded-md mt-4">
                <h3 className="font-medium mb-2">Why upgrade?</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Access to premium features like {featureName}</li>
                  <li>Higher usage limits for campaigns and brands</li>
                  <li>Priority customer support</li>
                  <li>Advanced analytics and reporting</li>
                </ul>
              </div>
            </div>
          </CardContent>
          <CardFooter>
            <Button onClick={() => navigate('/settings')}>
              View Subscription Options
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}